import ActivitiesUtil from 'utils/activity/ActivitiesUtil';
import Personalizer from 'personalizer';
import { formatRouteDifficulty, CLIMB_SPLIT_TYPES } from 'utils/indoorClimb/IndoorClimbUtil';
import {
    RUN_REST,
    RUN_ACTIVE,
    SKI_LIFT_SPLIT,
    SKI_RUN_SPLIT,
    SKI_DIFFICULTY_VALUES,
    DOWNHILL_BIKING_RUN_SPLIT,
    DOWNHILL_BIKING_LIFT_SPLIT,
    DYNAMIC_APNEA_DIVE,
    DYNAMIC_APNEA_REST,
    DYNAMIC_APNEA_STA
} from 'constants/TypedSplitsConstants';
import CadenceSensorTypes from 'utils/personalizer/CadenceSensorTypes';
import React from 'react';
import Translate from 'components/Translate';
import i18next from 'i18next';
import ActivityTypeMapper from 'utils/ActivityTypeMapper';

i18next.loadNamespaces(['diving', 'activity', 'units', 'common']);

const activityColors = ['#be5142', '#75a1cb', '#503dac', '#24ea59'];

const wakesportsExcludeMetrics = ['surfTotalWaves', 'surfLongestWave', 'surfTime', 'avgSpeedSurf'];
const surfExcludeMetrics = ['wakesportsTotalRuns', 'wakesportsLongestRun', 'activeTime', 'avgSpeedWakesportsRun'];

const ExcludableMetricsByTypeKey = {
    // surfing
    [ActivityTypeMapper.Surfing]: surfExcludeMetrics,
    [ActivityTypeMapper.SurfingV2]: surfExcludeMetrics,

    // wakesports
    [ActivityTypeMapper.Watertubing]: wakesportsExcludeMetrics,
    [ActivityTypeMapper.Wakeboarding]: wakesportsExcludeMetrics,
    [ActivityTypeMapper.WakeboardingV2]: wakesportsExcludeMetrics,
    [ActivityTypeMapper.Wakesurfing]: wakesportsExcludeMetrics,
    [ActivityTypeMapper.Waterskiing]: wakesportsExcludeMetrics
};

const surfingMetrics = [
    'surfTotalWaves',
    'surfLongestWave',
    'surfTime',
    'duration',
    'maxSpeedSurf',
    'avgSpeedSurf',
    'averageHR',
    'maxHR',
    'calories'
];

const wakesportsMetrics = [
    'wakesportsTotalRuns',
    'wakesportsLongestRun',
    'activeTime',
    'duration',
    'maxSpeedSurf',
    'avgSpeedWakesportsRun',
    'averageHR',
    'maxHR',
    'calories'
];

const offshoreGrindingMetrics = [
    'averageSpeed',
    'maxSpeed',
    'duration',
    'movingDuration',
    'averageHR',
    'maxHR',
    'averagePower',
    'maxPower',
    'averageBikeCadence',
    'maxBikeCadence',
    'calories',
    'distance'
];

const onshoreGrindingMetrics = [
    'averageSpeed',
    'maxSpeed',
    'duration',
    'movingDuration',
    'averageHR',
    'maxHR',
    'averagePower',
    'maxPower',
    'averageBikeCadence',
    'maxBikeCadence',
    'calories',
    'distance'
];

const resortSkiMetrics = [
    'elevationLoss',
    'distance',
    'resortSkiNoOfRuns',
    'maxSpeed',
    'averageSpeed',
    'duration',
    'runMovingTime',
    'liftTime'
];

const danceMetrics = ['maxHR', 'duration', 'avgTempo', 'steps', 'calories'];

const jumpRopeMetrics = ['calories', 'duration', 'totalReps', 'averageHR', 'avgJumpRopeCadence'];

/**
 * This controls the ordering of the metrics to appear at the top of the Compare page list. The other metrics will
 * appear after these in the order of getAllMetrics(). Also, if any of the activities being compared match these
 * activity types, then the ordering here is preserved for all compared activities even if they are another type.
 */
const downhillBikingMetrics = [
    'elevationLoss',
    'distance',
    'maxSpeed',
    'averageSpeed',
    'duration',
    'runMovingTime',
    'liftTime'
];

const MetricsForTypes = {
    indoor_climbing: [
        'noOfSplits',
        'maxGradeValue',
        'climbTime',
        'ascent',
        'maxAscent',
        'numClimbsAttempted',
        'numClimbsCompleted',
        'numClimbSends',
        'numFalls',
        'calories',
        'averageHR',
        'maxHR',
        'totalRestTime',
        'duration'
    ],

    bouldering: [
        'noOfSplits',
        'maxGradeValue',
        'climbTime',
        'numClimbsAttempted',
        'numClimbsCompleted',
        'calories',
        'averageHR',
        'maxHR',
        'totalRestTime',
        'duration'
    ],

    [ActivityTypeMapper.OffShoreGrinding]: offshoreGrindingMetrics,
    [ActivityTypeMapper.OffShoreGrindingV2]: offshoreGrindingMetrics,

    [ActivityTypeMapper.OnShoreGrinding]: onshoreGrindingMetrics,
    [ActivityTypeMapper.OnShoreGrindingV2]: onshoreGrindingMetrics,

    [ActivityTypeMapper.Surfing]: surfingMetrics,
    [ActivityTypeMapper.SurfingV2]: surfingMetrics,

    [ActivityTypeMapper.Esport]: ['duration', 'avgStress', 'averageHR', 'maxStress'],

    // wakesports
    [ActivityTypeMapper.Watertubing]: wakesportsMetrics,
    [ActivityTypeMapper.Wakeboarding]: wakesportsMetrics,
    [ActivityTypeMapper.WakeboardingV2]: wakesportsMetrics,
    [ActivityTypeMapper.Wakesurfing]: wakesportsMetrics,
    [ActivityTypeMapper.Waterskiing]: wakesportsMetrics,

    // resort skiing
    [ActivityTypeMapper.ResortSkiing]: resortSkiMetrics,
    [ActivityTypeMapper.ResortSnowboarding]: resortSkiMetrics,
    [ActivityTypeMapper.ResortSkiSnowboard]: resortSkiMetrics,

    // dance
    [ActivityTypeMapper.Dance]: danceMetrics,

    // jump rope
    [ActivityTypeMapper.JumpRope]: jumpRopeMetrics,

    [ActivityTypeMapper.Rucking]: ['distance', 'duration', 'beginPackWeight'],
    [ActivityTypeMapper.JumpRope]: jumpRopeMetrics,

    [ActivityTypeMapper.DownhillBiking]: downhillBikingMetrics
};

export default {
    modes() {
        return {
            Speed: 'Speed',
            Pace: 'Pace'
        };
    },

    getAllMetrics() {
        return {
            // diving
            maxDepth: {
                label: 'max_depth',
                ns: 'diving',
                personalize: (activity) =>
                    activity.maxDepth ? Personalizer.personalizeDepth(activity.maxDepth) : '--',
                unitKey: () => Personalizer.getDepthUnitKey()
            },
            bottomTime: {
                label: 'dive_time',
                ns: 'diving',
                personalize: (activity) =>
                    activity.bottomTime ? Personalizer.personalizeDuration(activity.bottomTime) : '--'
            },
            surfaceInterval: {
                label: 'surface_interval',
                ns: 'diving',
                personalize: (activity) =>
                    activity.surfaceInterval ? Personalizer.personalizeDuration(activity.surfaceInterval) : '--'
            },
            diveWeight: {
                label: 'dive_weight',
                ns: 'diving',
                key: 'diveInfo.weight',
                personalize: (activity) =>
                    activity.maxDepth ? Personalizer.personalizeDiveWeight(activity.diveInfo.weight) : '--',
                unitKey: (activity) =>
                    activity.diveInfo
                        ? Personalizer.personalizeDiveWeightUnit(activity.diveInfo.weightUnit, i18next)
                        : ''
            },
            waterCurrent: {
                label: 'water_current',
                ns: 'diving',
                key: 'diveInfo.current',
                personalize: (activity) => (activity.diveInfo ? activity.diveInfo.current : '--')
            },
            surfaceCondition: {
                label: 'surface_condition',
                ns: 'diving',
                key: 'diveInfo.surfaceCondition',
                personalize: (activity) => (activity.diveInfo ? activity.diveInfo.surfaceCondition : '--')
            },
            decompressionDive: {
                label: 'decompression_dive',
                ns: 'diving',
                key: 'diveInfo',
                personalize: (activity) => {
                    const label = activity.decoDive ? 'decompression_yes' : 'decompression_no';
                    return this.isDivingActivity(activity.activityTypeDTO.typeKey) ? (
                        <Translate ns="diving" content={label} />
                    ) : (
                        '--'
                    );
                }
            },
            diveVisibility: {
                label: 'dive_visibility',
                ns: 'diving',
                key: 'diveInfo.visibility',
                personalize: (activity) =>
                    activity.diveInfo && activity.diveInfo.visibility
                        ? Personalizer.personalizeVisibility(activity.diveInfo.visibility)
                        : '--',
                unitKey: (activity) => {
                    if (activity.diveInfo && activity.diveInfo.visibilityUnit) {
                        const label = Personalizer.personalizeVisibilityUnit(activity.diveInfo.visibilityUnit, i18next)
                            .replace('[', '')
                            .replace(']', '');
                        return <Translate ns="diving" content={label} />;
                    } else {
                        return '';
                    }
                }
            },
            waterType: {
                label: 'water_type',
                ns: 'diving',
                key: 'diveInfo.diveSettings',
                personalize: (activity) => {
                    if (activity.diveInfo && activity.diveInfo.diveSettings) {
                        const label = Personalizer.personalizeDiveWaterType(
                            activity.diveInfo.diveSettings.waterType,
                            activity.diveInfo.diveSettings.waterDensity,
                            i18next
                        )
                            .replace('[', '')
                            .replace(']', '');
                        return <Translate ns="diving" content={label} />;
                    } else {
                        return '--';
                    }
                }
            },
            totalDYNIntervals: {
                label: 'total_dyn_intervals',
                ns: 'diving',
                key: 'apneaDYNSplitSummary.noOfSplits',
                personalize: (activity) => {
                    const { noOfSplits } = this.getSplitSummary(activity, DYNAMIC_APNEA_DIVE);
                    return noOfSplits || '--';
                }
            },
            totalDYNTime: {
                label: 'total_dyn_time',
                ns: 'diving',
                key: 'apneaDYNSplitSummary.duration',
                personalize: (activity) => {
                    const { duration } = this.getSplitSummary(activity, DYNAMIC_APNEA_DIVE);
                    return duration ? Personalizer.personalizeDuration(duration) : '--';
                }
            },
            totalRESTIntervals: {
                label: 'total_REST_intervals',
                ns: 'diving',
                key: 'apneaRESTSplitSummary.noOfSplits',
                personalize: (activity) => {
                    const { noOfSplits } = this.getSplitSummary(activity, DYNAMIC_APNEA_REST);
                    return noOfSplits || '--';
                }
            },
            totalRESTTime: {
                label: 'total_REST_time',
                ns: 'diving',
                key: 'apneaRESTSplitSummary.duration',
                personalize: (activity) => {
                    const { duration } = this.getSplitSummary(activity, DYNAMIC_APNEA_REST);
                    return duration ? Personalizer.personalizeDuration(duration) : '--';
                }
            },
            totalSTAIntervals: {
                label: 'total_sta_intervals',
                ns: 'diving',
                key: 'apneaSTASplitSummary.noOfSplits',
                personalize: (activity) => {
                    const { noOfSplits } = this.getSplitSummary(activity, DYNAMIC_APNEA_STA);
                    return noOfSplits || '--';
                }
            },
            totalSTATime: {
                label: 'total_sta_time',
                ns: 'diving',
                key: 'apneaSTASplitSummary.duration',
                personalize: (activity) => {
                    const { duration } = this.getSplitSummary(activity, DYNAMIC_APNEA_STA);
                    return duration ? Personalizer.personalizeDuration(duration) : '--';
                }
            },
            // end diving
            distance: {
                label: 'label_distance',
                ns: 'units',
                personalize: (activity) => {
                    /**
                     * Resort Ski requires slightly different logic to get Ski Run data.
                     * Slightly breaking pattern in other Formatters with getResortSkiDistance, as Compare Page
                     * logic looks at field names and not at Activity Types to determine what to show
                     */
                    const hasSplits = activity?.metadataDTO?.hasSplits || activity?.hasSplits;
                    if (
                        (ActivitiesUtil.isResortSkiActivity(activity.activityTypeDTO.typeKey) ||
                            ActivitiesUtil.isDownhillBikingActivity(activity.activityTypeDTO.typeKey)) &&
                        hasSplits
                    ) {
                        const splitType = ActivitiesUtil.isResortSkiActivity(activity.activityTypeDTO.typeKey)
                            ? SKI_RUN_SPLIT
                            : DOWNHILL_BIKING_RUN_SPLIT;
                        const runDistance = this.getSplitSummary(activity, splitType).distance;
                        return Personalizer.personalizeDistance(
                            runDistance,
                            activity.activityTypeDTO.typeKey,
                            activity.unitOfPoolLength
                        );
                    }

                    return Personalizer.personalizeDistance(
                        activity.summaryDTO.distance,
                        activity.activityTypeDTO.typeKey,
                        activity.unitOfPoolLength
                    );
                },
                unitKey: (activity) => {
                    return Personalizer.getDistanceUnitKey(
                        activity.activityTypeDTO.typeKey,
                        activity.summaryDTO.distance,
                        activity.unitOfPoolLength
                    );
                }
            },
            duration: {
                label: (activity) => Personalizer.getDurationLabelKey(activity.activityTypeDTO.typeKey),
                ns: (activity) =>
                    ActivitiesUtil.isSurfingActivity(activity.activityTypeDTO.typeKey) ? 'activity' : 'units',
                personalize: (activity) => Personalizer.personalizeDuration(activity.summaryDTO.duration)
            },
            calories: {
                label: 'label_calories',
                ns: 'units',
                personalize: (activity) =>
                    activity.summaryDTO.calories ? Personalizer.formatWholeNumber(activity.summaryDTO.calories) : 0,
                unitKey: () => Personalizer.getCalorieUnitKey()
            },
            averageSpeed: {
                label: (activity) => Personalizer.getAvgSpeedTypeLabelKey(activity.activityTypeDTO.typeKey),
                ns: 'units',
                key: 'speedModel.avgSpeedOrPace',
                personalize: (activity) =>
                    activity.speedModel.avgSpeedOrPace ? activity.speedModel.avgSpeedOrPace : '--',
                unitKey: (activity) => activity.speedModel.avgSpeedOrPaceUnit
            },
            averageMovingSpeed: {
                label: (activity) => Personalizer.getAvgMovingSpeedTypeLabelKey(activity.activityTypeDTO.typeKey),
                ns: 'units',
                key: 'speedModel.avgMovingSpeedOrPace',
                personalize: (activity) =>
                    activity.speedModel.avgMovingSpeedOrPace ? activity.speedModel.avgMovingSpeedOrPace : '--',
                unitKey: (activity) => activity.speedModel.avgMovingSpeedOrPaceUnit
            },
            avgGradeAdjustedSpeed: {
                label: 'avg_grade_adjusted_pace',
                ns: 'gap',
                key: 'speedModel.avgGradeAdjustedSpeedOrPace',
                personalize: (activity) =>
                    activity.speedModel.avgGradeAdjustedSpeedOrPace
                        ? activity.speedModel.avgGradeAdjustedSpeedOrPace
                        : '--',
                unitKey: (activity) => activity.speedModel.avgGradeAdjustedSpeedOrPaceUnit
            },
            avgPace: {
                label: 'label_avg_pace',
                ns: 'units',
                personalize: (activity) =>
                    activity.speedModel.avgSpeedOrPace ? activity.speedModel.avgSpeedOrPace : '--',
                unitKey: (activity) => activity.speedModel.avgSpeedOrPaceUnit
            },
            avgMovingPace: {
                label: 'label_avg_moving_pace',
                ns: 'units',
                personalize: (activity) => activity.speedModel.avgMovingSpeedOrPace,
                unitKey: (activity) => activity.speedModel.avgMovingSpeedOrPaceUnit
            },
            maxSpeed: {
                label: (activity) => Personalizer.getMaxSpeedTypeLabelKey(activity.activityTypeDTO.typeKey),
                ns: 'units',
                personalize: (activity) =>
                    activity.movingDuration || activity.maxSpeed ? activity.speedModel.maxSpeedOrPace : '--',
                unitKey: (activity) => activity.speedModel.maxSpeedOrPaceUnit
            },
            maxPace: {
                label: 'label_max_pace',
                ns: 'units',
                key: 'speedPaceModel.paceModel.maxSpeedOrPace',
                personalize: (activity) =>
                    activity.movingDuration || activity.maxSpeed ? activity.speedModel.maxSpeedOrPace : '--',
                unitKey: (activity) => activity.speedModel.maxSpeedOrPaceUnit
            },
            runDuration: {
                label: 'label_active_time',
                ns: 'activity',
                key: 'runActiveSplitSummary.duration',
                personalize: (activity) => {
                    const runDuration = this.getSplitSummary(activity, RUN_ACTIVE).duration;
                    return runDuration ? Personalizer.personalizeDuration(runDuration) : '--';
                }
            },
            restDuration: {
                label: 'label_rest_time',
                ns: 'units',
                key: 'restSplitSummary.duration',
                personalize: (activity) => {
                    const restDuration = this.getSplitSummary(activity, RUN_REST).duration;
                    return restDuration ? Personalizer.personalizeDuration(restDuration) : '--';
                }
            },
            movingDuration: {
                label: 'label_moving_time',
                ns: 'units',
                personalize: (activity) =>
                    activity.movingDuration ? Personalizer.personalizeDuration(activity.movingDuration) : '--'
            },
            elapsedDuration: {
                label: 'label_elapsed_time',
                ns: 'units',
                personalize: (activity) =>
                    activity.summaryDTO.elapsedDuration
                        ? Personalizer.personalizeDuration(activity.summaryDTO.elapsedDuration)
                        : '--'
            },
            // resort ski
            runMovingTime: {
                label: 'label_run_moving_time',
                ns: 'activity',
                key: 'runMovingSplitSummary.duration',
                personalize: (activity) => {
                    const runDuration = this.getSplitSummary(activity, SKI_RUN_SPLIT).duration;
                    return runDuration ? Personalizer.personalizeDuration(runDuration) : '--';
                }
            },
            liftTime: {
                label: 'label_lift_time',
                ns: 'activity',
                key: 'liftSplitSummary.duration',
                personalize: (activity) => {
                    const runDuration = this.getSplitSummary(activity, SKI_LIFT_SPLIT).duration;
                    return runDuration ? Personalizer.personalizeDuration(runDuration) : '--';
                }
            },
            resortSkiNoOfRuns: {
                label: 'number_of_runs_ski',
                ns: 'activity',
                key: 'aliasModel.numberOfResortSkiRuns',
                personalize: (activity) =>
                    activity.aliasModel.numberOfResortSkiRuns ? activity.aliasModel.numberOfResortSkiRuns : '--'
            },
            elevationGain: {
                label: (activity) =>
                    ActivitiesUtil.isFloorClimbingActivity(activity.activityTypeDTO.typeKey)
                        ? 'label_elevation_gain'
                        : 'label_total_ascent',
                ns: 'units',
                personalize: (activity) => Personalizer.personalizeElevation(activity.elevationGain),
                unitKey: () => Personalizer.getElevationUnitKey()
            },
            elevationLoss: {
                label: (activity) =>
                    ActivitiesUtil.isFloorClimbingActivity(activity.activityTypeDTO.typeKey)
                        ? 'label_elevation_loss'
                        : 'label_total_descent',
                ns: 'units',
                personalize: (activity) => {
                    /**
                     * Resort Ski requires slightly different logic to get Ski Run data.
                     * Slightly breaking pattern in other Formatters with getResortSkiElevationLoss, as Compare Page
                     * logic looks at field names and not at Activity Types to determine what to show
                     */
                    const hasSplits = activity?.metadataDTO?.hasSplits || activity?.hasSplits;
                    if (
                        (ActivitiesUtil.isResortSkiActivity(activity.activityTypeDTO.typeKey) ||
                            ActivitiesUtil.isDownhillBikingActivity(activity.activityTypeDTO.typeKey)) &&
                        hasSplits
                    ) {
                        const splitType = ActivitiesUtil.isResortSkiActivity(activity.activityTypeDTO.typeKey)
                            ? SKI_RUN_SPLIT
                            : DOWNHILL_BIKING_RUN_SPLIT;
                        const runElevationLoss = this.getSplitSummary(activity, splitType).elevationLoss;
                        return Personalizer.personalizeElevation(runElevationLoss);
                    }
                    return Personalizer.personalizeElevation(activity.elevationLoss);
                },
                unitKey: () => Personalizer.getElevationUnitKey()
            },
            minElevation: {
                label: 'label_min_elevation',
                ns: 'units',
                personalize: (activity) => Personalizer.personalizeElevation(activity.minElevation),
                unitKey: () => Personalizer.getElevationUnitKey()
            },
            maxElevation: {
                label: 'label_max_elevation',
                ns: 'units',
                personalize: (activity) => Personalizer.personalizeElevation(activity.maxElevation),
                unitKey: () => Personalizer.getElevationUnitKey()
            },
            averageHR: {
                label: 'label_avg_HR',
                ns: 'units',
                personalize: (activity) =>
                    activity.averageHR ? Personalizer.formatWholeNumber(activity.averageHR) : '--',
                unitKey: () => Personalizer.getHeartRateUnitKey()
            },
            maxHR: {
                label: 'label_max_HR',
                ns: 'units',
                personalize: (activity) => (activity.maxHR ? Personalizer.formatWholeNumber(activity.maxHR) : '--'),
                unitKey: () => Personalizer.getHeartRateUnitKey()
            },
            avgWheelchairCadence: {
                label: 'label_avg_wheelchair_cadence',
                ns: 'units',
                personalize: (activity) =>
                    activity.avgWheelchairCadence
                        ? Personalizer.personalizeCadence(activity.avgWheelchairCadence)
                        : '--',
                unitKey: (activity) => Personalizer.getCadenceUnitKey(activity.activityTypeDTO.typeKey)
            },
            maxWheelchairCadence: {
                label: 'label_max_wheelchair_cadence',
                ns: 'units',
                personalize: (activity) =>
                    activity.avgWheelchairCadence
                        ? Personalizer.personalizeCadence(activity.avgWheelchairCadence)
                        : '--',
                unitKey: (activity) => Personalizer.getCadenceUnitKey(activity.activityTypeDTO.typeKey)
            },
            averageRunCadence: {
                label: 'label_avg_run_cadence',
                ns: 'units',
                personalize: (activity) => {
                    return activity.averageRunCadence &&
                        Personalizer.getCadenceSensorTypeFromActivityType(activity.activityTypeDTO.typeKey) ===
                            CadenceSensorTypes.Ped
                        ? Personalizer.personalizeCadence(activity.averageRunCadence)
                        : '--';
                },
                unitKey: (activity) => Personalizer.getCadenceUnitKey(activity.activityTypeDTO.typeKey)
            },
            maxRunCadence: {
                label: 'label_max_run_cadence',
                ns: 'units',
                personalize: (activity) => {
                    return activity.maxRunCadence &&
                        Personalizer.getCadenceSensorTypeFromActivityType(activity.activityTypeDTO.typeKey) ===
                            CadenceSensorTypes.Ped
                        ? Personalizer.personalizeCadence(activity.maxRunCadence)
                        : '--';
                },
                unitKey: (activity) => Personalizer.getCadenceUnitKey(activity.activityTypeDTO.typeKey)
            },
            beginPackWeight: {
                label: 'label_pack_weight',
                ns: 'units',
                personalize: (activity) => {
                    const packWeight = activity.beginPackWeight;
                    return packWeight
                        ? Personalizer.personalizeWeight(packWeight, Personalizer.NumberFormats.NO_DECIMAL)
                        : '--';
                },
                unitKey: (activity) => {
                    const packWeight = activity.beginPackWeight;
                    return packWeight ? i18next.t(`units:${Personalizer.getWeightUnitKey()}`) : '--';
                }
            },
            averageTemperature: {
                label: 'label_avg_temp',
                ns: 'units',
                personalize: (activity) =>
                    isNaN(activity.averageTemperature)
                        ? '--'
                        : Personalizer.personalizeTemperature(activity.averageTemperature),
                unitKey: () => Personalizer.getTemperatureUnitKey()
            },
            minTemperature: {
                label: (activity) =>
                    this.isDivingActivity(activity.activityTypeDTO.typeKey)
                        ? 'min_water_temperature'
                        : 'label_min_temp',
                ns: (activity) =>
                    ActivitiesUtil.isDivingActivity(activity.activityTypeDTO.typeKey) ? 'diving' : 'units',
                personalize: (activity) =>
                    isNaN(activity.minTemperature)
                        ? '--'
                        : Personalizer.personalizeTemperature(activity.minTemperature),
                unitKey: () => Personalizer.getTemperatureUnitKey()
            },
            maxTemperature: {
                label: 'label_max_temp',
                ns: 'units',
                personalize: (activity) =>
                    isNaN(activity.maxTemperature)
                        ? '--'
                        : Personalizer.personalizeTemperature(activity.maxTemperature),
                unitKey: () => Personalizer.getTemperatureUnitKey()
            },
            bestLap: {
                label: 'marq_best_laptime',
                ns: 'activity',
                personalize: (activity) => Personalizer.fromSecondsToDuration(activity.minActivityLapDuration)
            },
            totalTime: {
                label: 'label_time_total',
                ns: 'units',
                personalize: (activity) => Personalizer.fromSecondsToDuration(activity.autoRacingDuration)
            },
            numberOfRuns: {
                label: 'number_of_runs',
                ns: 'activity',
                personalize: (activity) => Personalizer.formatWholeNumber(activity.numberOfRuns) || '--'
            },
            numberOfLaps: {
                label: 'marq_number_laps',
                ns: 'activity',
                personalize: (activity) => Personalizer.formatWholeNumber(activity.numberOfLaps) || '--'
            },
            averageStrokes: {
                label: 'label_strokes',
                ns: 'units',
                personalize: (activity) =>
                    activity.averageStrokes ? Personalizer.personalizeCadence(activity.averageStrokes) : '--',
                unitKey: (activity) => Personalizer.getCadenceUnitKey(activity.activityTypeDTO.typeKey)
            },
            averageSwimCadence: {
                label: 'label_strokes',
                ns: 'units',
                personalize: (activity) =>
                    activity.averageSwimCadence ? Personalizer.personalizeCadence(activity.averageSwimCadence) : '--',
                unitKey: (activity) => Personalizer.getCadenceUnitKey(activity.activityTypeDTO.typeKey)
            },
            totalNumberOfStrokes: {
                label: (activity) => Personalizer.getTotalStrokesLabelKey(activity.activityTypeDTO.typeKey),
                ns: 'units',
                personalize: (activity) => Personalizer.personalizeCadence(activity?.totalNumberOfStrokes),
                unitKey: ''
            },
            verticalOscillation: {
                label: 'label_avg_vertical_oscillation',
                ns: 'units',
                personalize: (activity) =>
                    activity.verticalOscillation
                        ? Personalizer.personalizeVerticalOscillation(activity.verticalOscillation)
                        : '--',
                unitKey: () => Personalizer.getVerticalOscillationUnitKey()
            },
            groundContactTime: {
                label: 'label_avg_ground_contact_time',
                ns: 'units',
                personalize: (activity) =>
                    activity.groundContactTime
                        ? Personalizer.personalizeGroundContactTime(activity.groundContactTime)
                        : '--',
                unitKey: () => Personalizer.getGroundContactTimeUnitKey()
            },
            strideLength: {
                label: 'label_avg_stride_length',
                ns: 'units',
                personalize: (activity) =>
                    activity.strideLength ? Personalizer.personalizeAvgStrideLength(activity.strideLength) : '--',
                unitKey: () => Personalizer.getAvgStrideLengthUnitKey()
            },
            trainingEffect: {
                label: 'label_training_effect',
                ns: 'units',
                personalize: (activity) => Personalizer.personalizeTrainingEffect(activity.trainingEffect)
            },
            maxDifficulty: {
                label: 'label_max_difficulty',
                ns: 'activity',
                key: 'runMovingSplitSummary.maxPisteDifficulty',
                personalize: (activity) => {
                    const maxDifficulty = this.getSplitSummary(activity, SKI_RUN_SPLIT).maxPisteDifficulty;
                    const highestDifficultyKey = SKI_DIFFICULTY_VALUES[maxDifficulty]?.value;
                    return highestDifficultyKey ? i18next.t(`activity:${highestDifficultyKey}`) : '--';
                }
            },
            averageBikeCadence: {
                label: (activity) => Personalizer.getAvgCadenceTypeLabelKey(activity.activityTypeDTO.typeKey),
                ns: 'units',
                personalize: (activity) => {
                    return activity.averageBikeCadence
                        ? Personalizer.personalizeCadence(activity.averageBikeCadence)
                        : '--';
                },
                unitKey: (activity) => Personalizer.getCadenceUnitKey(activity.activityTypeDTO.typeKey)
            },
            maxBikeCadence: {
                label: (activity) => Personalizer.getMaxCadenceTypeLabelKey(activity.activityTypeDTO.typeKey),
                ns: 'units',
                personalize: (activity) =>
                    activity.maxBikeCadence ? Personalizer.personalizeCadence(activity.maxBikeCadence) : '--',
                unitKey: (activity) => Personalizer.getCadenceUnitKey(activity.activityTypeDTO.typeKey)
            },
            averagePower: {
                label: 'label_avg_power',
                ns: 'units',
                personalize: (activity, runningPowerEnabled) => {
                    const isRunning = ActivitiesUtil.isRunningActivity(activity.activityTypeDTO.typeKey);
                    return activity.averagePower && (!isRunning || runningPowerEnabled)
                        ? Personalizer.personalizePower(activity.averagePower)
                        : '--';
                },
                unitKey: () => Personalizer.getPowerUnitKey()
            },
            maxPower: {
                label: 'label_max_power',
                ns: 'units',
                personalize: (activity, runningPowerEnabled) => {
                    const isRunning = ActivitiesUtil.isRunningActivity(activity.activityTypeDTO.typeKey);
                    return activity.maxPower && (!isRunning || runningPowerEnabled)
                        ? Personalizer.personalizePower(activity.maxPower)
                        : '--';
                },
                unitKey: () => Personalizer.getPowerUnitKey()
            },
            windData: {
                label: 'wind_data',
                ns: 'power_zone',
                personalize: (activity) => {
                    const { runPowerWindDataEnabled, hasRunPowerWindData } = activity.metadataDTO;
                    if (runPowerWindDataEnabled && hasRunPowerWindData) {
                        return i18next.t('common:enabled');
                    } else if (runPowerWindDataEnabled) {
                        return i18next.t('common:unavailable');
                    } else if (runPowerWindDataEnabled === false) {
                        return i18next.t('common:disabled');
                    } else {
                        return '--';
                    }
                }
            },
            maxPowerTwentyMinutes: {
                label: 'label_max_avg_power',
                ns: 'units',
                personalize: (activity) =>
                    activity.maxPowerTwentyMinutes
                        ? Personalizer.personalizePower(activity.maxPowerTwentyMinutes)
                        : '--',
                unitKey: () => Personalizer.getPowerUnitKey()
            },
            totalReps: {
                label: 'label_total_reps',
                ns: 'units',
                key: 'exerciseSets',
                personalize: (activity) => Personalizer.zeroDash(this.getTotalRepCount(activity))
            },
            totalSets: {
                label: 'label_total_exercises',
                ns: 'units',
                key: 'exerciseSets',
                personalize: (activity) => Personalizer.zeroDash(this.getActiveSetCount(activity))
            },
            avgRespirationRate: {
                label: 'label_average_respiration_rate',
                ns: 'units',
                personalize: (activity) => Personalizer.personalizeRespiration(activity.avgRespirationRate),
                unitKey: () => Personalizer.getRespirationUnitKey()
            },
            minRespirationRate: {
                label: 'label_min_respiration_rate',
                ns: 'units',
                personalize: (activity) => Personalizer.personalizeRespiration(activity.minRespirationRate),
                unitKey: () => Personalizer.getRespirationUnitKey()
            },
            maxRespirationRate: {
                label: 'label_max_respiration_rate',
                ns: 'units',
                personalize: (activity) => Personalizer.personalizeRespiration(activity.maxRespirationRate),
                unitKey: () => Personalizer.getRespirationUnitKey()
            },
            avgStress: {
                label: 'label_avg_stress',
                ns: 'units',
                personalize: (activity) => Personalizer.personalizeStress(activity.avgStress)
            },
            maxStress: {
                label: 'label_max_stress',
                ns: 'units',
                personalize: (activity) => Personalizer.personalizeStress(activity.maxStress)
            },
            startStress: {
                label: 'label_stress_start',
                ns: 'units',
                personalize: (activity) => Personalizer.personalizeStress(activity.startStress)
            },
            endStress: {
                label: 'label_stress_end',
                ns: 'units',
                personalize: (activity) => Personalizer.personalizeStress(activity.endStress)
            },
            differenceStress: {
                label: 'label_stress_change',
                ns: 'units',
                personalize: (activity) => Personalizer.personalizeStress(activity.differenceStress)
            },
            leftBalance: {
                label: 'label.leftRightBalance',
                ns: 'activity-api',
                personalize: (activity) => {
                    const leftBalance = Personalizer.personalizeLeftRightBalance(activity.leftBalance);
                    const rightBalance = Personalizer.personalizeLeftRightBalance(100 - leftBalance);
                    return activity.leftBalance ? (
                        <Translate
                            key={Personalizer.getPowerBalanceUnitKey()}
                            ns="units"
                            content={Personalizer.getPowerBalanceUnitKey()}
                            params={{ 0: leftBalance, 1: rightBalance }}
                        />
                    ) : (
                        '-- L / -- R'
                    );
                }
            },
            normalizedPower: {
                label: 'label_normalized_power',
                ns: 'units',
                personalize: (activity) => {
                    const isRunning = ActivitiesUtil.isRunningActivity(activity.activityTypeDTO.typeKey);
                    return activity.normalizedPower && !isRunning
                        ? Personalizer.personalizePower(activity.normalizedPower)
                        : '--';
                },
                unitKey: () => Personalizer.getPowerUnitKey()
            },
            intensityFactor: {
                label: 'label_intensity_factor',
                ns: 'units',
                personalize: (activity) =>
                    activity.intensityFactor ? Personalizer.personalizeIntensityFactor(activity.intensityFactor) : '--'
            },
            trainingStressScore: {
                label: 'label_training_stress_score',
                ns: 'units',
                personalize: (activity) =>
                    activity.trainingStressScore ? Personalizer.personalizeTSS(activity.trainingStressScore) : '--'
            },
            noOfSplits: {
                // typedSplits aka routes
                label: 'total_climb_routes',
                ns: 'activity',
                key: 'climbSummary.noOfSplits',
                personalize: (activity) => Personalizer.formatWholeNumber(activity.climbSummary.noOfSplits)
            },
            maxGradeValue: {
                label: 'label_max_difficulty',
                ns: 'activity',
                key: 'climbSummary.maxGradeValue',
                personalize: (activity, indoorClimbDifficulty) => {
                    if (!activity.climbSummary || (activity.climbSummary && !activity.climbSummary.maxGradeValue)) {
                        return '--';
                    }
                    return formatRouteDifficulty(
                        activity.climbSummary.maxGradeValue.scale,
                        activity.climbSummary.maxGradeValue.valueKey,
                        indoorClimbDifficulty
                    );
                }
            },
            climbTime: {
                // splitSummary duration
                label: 'climb_active_time',
                ns: 'activity',
                key: 'climbSummary.duration',
                personalize: (activity) =>
                    activity.climbSummary.duration
                        ? Personalizer.personalizeDuration(activity.climbSummary.duration)
                        : '--'
            },
            ascent: {
                // split summary elevation gain
                label: 'activity_total_ascent',
                ns: 'activity',
                key: 'climbSummary.elevationGain',
                personalize: (activity) => Personalizer.personalizeElevation(activity.climbSummary.elevationGain),
                unitKey: () => Personalizer.getElevationUnitKey()
            },
            maxAscent: {
                // split summary maxElevationGain
                label: 'climb_max_ascent',
                ns: 'activity',
                key: 'climbSummary.maxElevationGain',
                personalize: (activity) => Personalizer.personalizeElevation(activity.climbSummary.maxElevationGain),
                unitKey: () => Personalizer.getElevationUnitKey()
            },
            numClimbsAttempted: {
                label: 'climb_routes_attempted',
                ns: 'activity',
                key: 'climbSummary.numClimbsAttempted',
                personalize: (activity) => Personalizer.formatWholeNumber(activity.climbSummary.numClimbsAttempted)
            },
            numClimbsCompleted: {
                label: 'climb_routes_completed',
                ns: 'activity',
                key: 'climbSummary.numClimbsCompleted',
                personalize: (activity) => Personalizer.formatWholeNumber(activity.climbSummary.numClimbsCompleted)
            },
            numClimbSends: {
                label: 'climb_sends',
                ns: 'activity',
                key: 'climbSummary.numClimbSends',
                personalize: (activity) => Personalizer.formatWholeNumber(activity.climbSummary.numClimbSends)
            },
            numFalls: {
                label: 'climb_falls',
                ns: 'activity',
                key: 'climbSummary.numFalls',
                personalize: (activity) => Personalizer.formatWholeNumber(activity.climbSummary.numFalls)
            },
            surfTotalWaves: {
                label: 'total_waves_surf',
                ns: 'activity',
                key: 'surfSummary.noOfSplits',
                personalize: (activity) => {
                    const { noOfSplits } = this.getSplitSummary(activity, 'SURF_ACTIVE');
                    return noOfSplits || '--';
                }
            },
            // same as surfTotalWaves, except label
            wakesportsTotalRuns: {
                label: 'total_runs_wakesports',
                ns: 'activity',
                key: 'surfSummary.noOfSplits',
                personalize: (activity) => {
                    const { noOfSplits } = this.getSplitSummary(activity, 'SURF_ACTIVE');
                    return noOfSplits || '--';
                }
            },
            surfLongestWave: {
                label: 'surf_longest_wave_label',
                ns: 'activity',
                key: 'surfSummary.maxDistance',
                personalize: (activity) => {
                    const { maxDistance } = this.getSplitSummary(activity, 'SURF_ACTIVE');
                    return maxDistance ? Personalizer.personalizeWaveDistance(maxDistance) : '--';
                },
                unitKey: () => Personalizer.getWaveDistanceUnitKey()
            },
            // same as surfLongestWave, except label
            wakesportsLongestRun: {
                label: 'wakesports_longest_run_label',
                ns: 'activity',
                key: 'surfSummary.maxDistance',
                personalize: (activity) => {
                    const { maxDistance } = this.getSplitSummary(activity, 'SURF_ACTIVE');
                    return maxDistance ? Personalizer.personalizeWaveDistance(maxDistance) : '--';
                },
                unitKey: () => Personalizer.getWaveDistanceUnitKey()
            },
            surfTime: {
                label: 'surf_time_label',
                ns: 'activity',
                key: 'surfSummary.duration',
                personalize: (activity) => {
                    const { duration } = this.getSplitSummary(activity, 'SURF_ACTIVE');
                    return duration ? Personalizer.personalizeDuration(duration) : '--';
                }
            },
            // same as surfTime, except label
            activeTime: {
                label: 'label_active_time',
                ns: 'activity',
                key: 'surfSummary.duration',
                personalize: (activity) => {
                    const { duration } = this.getSplitSummary(activity, 'SURF_ACTIVE');
                    return duration ? Personalizer.personalizeDuration(duration) : '--';
                }
            },
            maxSpeedSurf: {
                label: 'max_speed_surf',
                ns: 'activity',
                key: 'surfSummary.maxSpeed',
                personalize: (activity) =>
                    Personalizer.personalizeSpeed(this.getSplitSummary(activity, 'SURF_ACTIVE').maxSpeed),
                unitKey: () => Personalizer.getSpeedTypeKey()
            },
            avgSpeedSurf: {
                label: 'avg_surf_speed',
                ns: 'activity',
                key: 'surfSummary.averageSpeed',
                personalize: (activity) => {
                    const { averageSpeed } = this.getSplitSummary(activity, 'SURF_ACTIVE');
                    return averageSpeed ? Personalizer.personalizeSpeed(averageSpeed) : '--';
                },
                unitKey: () => Personalizer.getSpeedTypeKey()
            },
            // same as avgSpeedSurf, except label
            avgSpeedWakesportsRun: {
                label: 'avg_run_speed',
                ns: 'activity',
                key: 'surfSummary.averageSpeed',
                personalize: (activity) => {
                    const { averageSpeed } = this.getSplitSummary(activity, 'SURF_ACTIVE');
                    return averageSpeed ? Personalizer.personalizeSpeed(averageSpeed) : '--';
                },
                unitKey: () => Personalizer.getSpeedTypeKey()
            },
            totalRestTime: {
                label: 'total_rest_time',
                ns: 'activity',
                key: 'totalRestTime',
                personalize: (activity) => {
                    let splitSummary = this.getActivityRestTime(activity, activity.activityTypeDTO.typeKey);

                    if (this.isHiitActivity(activity.activityTypeDTO.typeKey)) {
                        // Hiit Rest Time is calculated from SummaryDTO data
                        return Personalizer.personalizeDuration(activity.summaryDTO.duration - activity.movingDuration);
                    } else if (splitSummary && splitSummary.splitType === CLIMB_SPLIT_TYPES.CLIMB_REST) {
                        // Indoor Climb / Bouldering Rest Time comes from the SplitSummaries data
                        return Personalizer.personalizeDuration(splitSummary.duration);
                    }
                    return '--';
                }
            },
            // dance
            avgTempo: {
                label: 'label_avg_tempo',
                ns: 'activity',
                personalize: (activity) =>
                    ActivitiesUtil.isDanceActivity(activity.activityTypeDTO.typeKey) && activity.averageRunCadence
                        ? Personalizer.personalizeCadence(activity.averageRunCadence)
                        : '--',
                unitKey: (activity) => Personalizer.getCadenceUnitKey(activity.activityTypeDTO.typeKey)
            },
            steps: {
                label: 'label_steps',
                ns: 'units',
                personalize: (activity) => {
                    /* per requirements in CA-181298/CA-190050, only Walk/Hike/Dance shows steps */
                    if (
                        ActivitiesUtil.isDanceActivity(activity.activityTypeDTO.typeKey) ||
                        ActivitiesUtil.isWalkingActivity(activity.activityTypeDTO.typeKey) ||
                        ActivitiesUtil.isHikingActivity(activity.activityTypeDTO.typeKey)
                    ) {
                        return activity.steps ? Personalizer.formatWholeNumber(activity.steps) : '--';
                    } else {
                        return '--';
                    }
                }
            },
            // jump rope
            avgJumpRopeCadence: {
                label: 'label_speed',
                ns: 'units',
                personalize: (activity) =>
                    activity.avgJumpRopeCadence ? Personalizer.formatWholeNumber(activity.avgJumpRopeCadence) : '--',
                unitKey: (activity) => Personalizer.getCadenceUnitKey(activity.activityTypeDTO.typeKey)
            }
        };
    },

    getAliasesModelForActivity(activityJson) {
        let aliasModel = {};
        if (
            ActivitiesUtil.isResortSkiActivity(activityJson.activityTypeDTO.typeKey) ||
            ActivitiesUtil.isDownhillBikingActivity(activityJson.activityTypeDTO.typeKey)
        ) {
            const splitType = ActivitiesUtil.isResortSkiActivity(activityJson.activityTypeDTO.typeKey)
                ? SKI_LIFT_SPLIT
                : DOWNHILL_BIKING_LIFT_SPLIT;
            const hasSplits = activityJson?.metadataDTO?.hasSplits || activityJson?.hasSplits;
            if (hasSplits) {
                aliasModel.numberOfResortSkiRuns = this.getSplitSummary(activityJson, splitType).noOfSplits;
            } else {
                aliasModel.numberOfResortSkiRuns = activityJson?.metadataDTO?.lapCount;
            }
        }

        if (ActivitiesUtil.isAutoRacingActivity(activityJson.activityTypeDTO.typeKey)) {
            aliasModel.numberOfLaps = activityJson.lapCount;
            aliasModel.autoRacingDuration = activityJson.duration;
        }

        return aliasModel;
    },

    isRepCounting: function(activityTypeKey) {
        return ActivitiesUtil.isRepCounting(activityTypeKey);
    },

    // Used in activitiesActions file to make API call
    isHiitActivity: function(activityTypeKey) {
        return ActivitiesUtil.isHiitActivity(activityTypeKey);
    },

    isDivingActivity: function(typeKey) {
        return ActivitiesUtil.isDivingActivity(typeKey);
    },

    // Get Total Rest Time
    getActivityRestTime: function(activity, activityTypeKey) {
        if (
            ActivitiesUtil.isIndoorClimbingActivity(activityTypeKey) ||
            ActivitiesUtil.isBoulderingActivity(activityTypeKey)
        ) {
            return this.getSplitSummary(activity, CLIMB_SPLIT_TYPES.CLIMB_REST);
        } else if (this.isHiitActivity(activityTypeKey)) {
            return Personalizer.personalizeDuration(activity.summaryDTO.duration - activity.movingDuration);
        }
    },

    getTotalRepCount: function(activity) {
        const { typeKey } = activity.activityTypeDTO;
        let totalReps = 0;

        if (ActivitiesUtil.isJumpRopeActivity(typeKey)) {
            totalReps = activity.totalReps ?? 0;
        } else {
            const allSets = activity.exerciseSets;

            if (allSets) {
                for (let i = 0, length = allSets.length; i < length; i++) {
                    if (allSets[i].setType !== 'REST') {
                        totalReps += allSets[i].repetitionCount;
                    }
                }
            }
        }

        return totalReps;
    },

    getActiveSetCount: function(activity) {
        const allSets = activity.exerciseSets;
        let exerciseSets = 0;

        if (allSets) {
            for (let i = 0, length = allSets.length; i < length; i++) {
                if (allSets[i].setType !== 'REST') {
                    exerciseSets++;
                }
            }
        } else {
            exerciseSets += 1; // we will display a temporary set if they don't have any sets
        }

        return exerciseSets;
    },

    getPropertyByCombinedKey(path, obj) {
        return path.split('.').reduce(function(prev, curr) {
            return prev ? prev[curr] : null;
        }, obj);
    },

    getActivityColor(index) {
        return activityColors[index];
    },

    getMetricsForActivityType(activities) {
        let allMetrics = this.getAllMetrics();
        let activityMetrics = {};
        // put top metrics for the activity type up top
        activities.forEach((activity) => {
            let metricsForType = MetricsForTypes[activity.activityTypeDTO.typeKey];
            if (metricsForType) {
                metricsForType.forEach((metricForType) => {
                    if (activityMetrics[metricForType] === undefined) {
                        activityMetrics[metricForType] = allMetrics[metricForType];
                    }
                });
            }
        });

        const allTypeKeys = activities.map((activity) => activity.activityTypeDTO.typeKey);
        const shouldExclude = (key) =>
            allTypeKeys.some(
                (typeKey) => ExcludableMetricsByTypeKey[typeKey] && ExcludableMetricsByTypeKey[typeKey].includes(key)
            );

        // fill in remaining metrics
        let allMetricKeys = Object.keys(allMetrics);
        allMetricKeys.forEach((key) => {
            if (!shouldExclude(key) && activityMetrics[key] === undefined) {
                activityMetrics[key] = allMetrics[key];
            }
        });

        return activityMetrics;
    },

    getSplitSummary(activity, splitType) {
        if (activity.splitSummaries) {
            let splitSummary = activity.splitSummaries.find((splitSummary) => {
                return splitSummary.splitType === splitType;
            });
            return splitSummary || {};
        }

        return {};
    },

    /**
     * Some activities do not support laps (indoor climbing for example)
     */
    supportsLaps(activityType) {
        if (
            ActivitiesUtil.isIndoorClimbingActivity(activityType) ||
            ActivitiesUtil.isBoulderingActivity(activityType) ||
            ActivitiesUtil.isSurfingActivity(activityType) ||
            ActivitiesUtil.isEsportActivity(activityType) ||
            ActivitiesUtil.isDivingActivity(activityType) ||
            ActivitiesUtil.isHiitActivity(activityType) ||
            ActivitiesUtil.isMeditationActivity(activityType) ||
            ActivitiesUtil.isBreathworkActivity(activityType)
        ) {
            return false;
        }
        return true;
    }
};

const pressureUnit = Personalizer.isMetricMeasurementSystem() ? 'bar' : 'psi';

export const translationKeys = [
    {
        dataKey: 'directElevation',
        unitKey: Personalizer.getElevationUnitKey(),
        labelKey: 'label_elevation',
        ns: 'units'
    },
    {
        dataKey: 'directSpeed',
        unitKey: Personalizer.getSpeedUnitKey(),
        labelKey: 'label_speed',
        ns: 'units'
    },
    {
        dataKey: 'directGradeAdjustedSpeed',
        unitKey: Personalizer.getSpeedUnitKey(),
        labelKey: 'avg_grade_adjusted_pace',
        ns: 'gap'
    },
    {
        dataKey: 'directHeartRate',
        unitKey: Personalizer.getHeartRateUnitKey(),
        labelKey: 'label_heart_rate',
        ns: 'units'
    },
    {
        dataKey: 'directPotentialStamina',
        unitKey: 'percentage_unit',
        labelKey: 'real_time_stamina_stamina_potential',
        ns: 'activity'
    },
    {
        dataKey: 'directPerformanceCondition',
        unitKey: '',
        labelKey: 'label_performance_condition',
        ns: 'units'
    },
    {
        dataKey: 'directStrideLength',
        unitKey: Personalizer.getAvgStrideLengthUnitKey(),
        labelKey: 'label_stride_length',
        ns: 'units'
    },
    {
        dataKey: 'directDoubleCadence',
        unitKey: 'run_cadence_unit',
        labelKey: 'label_cadence',
        ns: 'units'
    },
    {
        dataKey: 'directPower',
        unitKey: Personalizer.getPowerUnitKey(),
        labelKey: 'label_power',
        ns: 'units'
    },
    {
        dataKey: 'directVerticalOscillation',
        unitKey: Personalizer.getVerticalOscillationUnitKey(),
        labelKey: 'label_vert_oscillation',
        ns: 'units'
    },
    {
        dataKey: 'directGroundContactTime',
        unitKey: Personalizer.getGroundContactTimeUnitKey(),
        labelKey: 'label_ground_contact_time',
        ns: 'units'
    },
    {
        dataKey: 'directGroundContactBalanceLeft',
        unitKey: 'percentage_unit',
        labelKey: 'label_ground_contact_time_balance',
        ns: 'units'
    },
    {
        dataKey: 'directRespirationRate',
        unitKey: 'label_respiration_rate_unit',
        labelKey: 'label_respiration_rate',
        ns: 'units'
    },
    {
        dataKey: 'directAirTemperature',
        unitKey: Personalizer.getTemperatureUnitKey(),
        labelKey: 'label_temperature',
        ns: 'units'
    },
    {
        dataKey: 'directBikeCadence',
        unitKey: 'bike_cadence_unit',
        labelKey: 'label_bike_cadence',
        ns: 'units'
    },
    {
        dataKey: 'directTimestamp',
        unitKey: 'label_time_total',
        labelKey: 'label_time',
        ns: 'units'
    },
    {
        dataKey: 'directRightBalance',
        unitKey: '',
        labelKey: 'right_balance',
        ns: 'activity'
    },
    {
        dataKey: 'directLeftPlatformCenterOffset',
        unitKey: 'platform_center_offset_unit',
        labelKey: 'label_cd_left_pedal_center_offset',
        ns: 'activity'
    },
    {
        dataKey: 'directRightPlatformCenterOffset',
        unitKey: 'platform_center_offset_unit',
        labelKey: 'label_cd_right_pedal_center_offset',
        ns: 'activity'
    },
    {
        dataKey: 'directLeftPowerPhaseStart',
        unitKey: 'degree_unit',
        labelKey: 'label_cd_l_power_phase_start',
        ns: 'units'
    },
    {
        dataKey: 'directLeftPowerPhasePeakStart',
        unitKey: 'degree_unit',
        labelKey: 'label_cd_l_peak_power_phase',
        ns: 'units'
    },
    {
        dataKey: 'directLeftPowerPhaseEnd',
        unitKey: 'degree_unit',
        labelKey: 'label_cd_l_power_phase',
        ns: 'units'
    },
    {
        dataKey: 'directLeftPowerPhasePeakEnd',
        unitKey: 'degree_unit',
        labelKey: 'label_cd_l_peak_power_phase',
        ns: 'units'
    },
    {
        dataKey: 'directFlow',
        unitKey: '',
        labelKey: 'label_flow',
        ns: 'activity'
    },
    {
        dataKey: 'directGrit',
        unitKey: '',
        labelKey: 'label_grit',
        ns: 'activity'
    },
    {
        dataKey: 'weightedMeanSpeed',
        unitKey: Personalizer.getSwimPaceUnitKey(),
        labelKey: 'label_avg_moving_speed',
        ns: 'units'
    },
    {
        dataKey: 'sumStrokes',
        unitKey: Personalizer.getStrokesUnitKey(),
        labelKey: 'label_strokes',
        ns: 'units'
    },
    {
        dataKey: 'weightedMeanSwolf',
        unitKey: 'label_swolf',
        labelKey: 'label_swolf',
        ns: 'units'
    },
    {
        dataKey: 'directSwimCadence',
        unitKey: '',
        labelKey: 'label_strokes',
        ns: 'units'
    },
    {
        dataKey: 'directDepth',
        unitKey: Personalizer.getDepthUnitKey(),
        labelKey: 'label_depth_profile',
        ns: 'diving'
    },
    {
        dataKey: 'tankPressure',
        unitKey: Personalizer.personalizePressureUnitKey({ unitKey: pressureUnit }),
        labelKey: 'label_tank_pressure',
        ns: 'diving'
    },
    {
        dataKey: 'directVerticalSpeed',
        unitKey: '',
        labelKey: Personalizer.isMetricMeasurementSystem() ? 'label_pace_unit_metric' : 'label_pace_unit_statute',
        ns: 'units'
    },
    {
        dataKey: 'directCurrentStress',
        unitKey: '',
        labelKey: 'label_stress',
        ns: 'stress'
    },
    {
        dataKey: 'directStrokeCadence',
        unitKey: 'rowing_cadence_unit',
        labelKey: 'stroke_cadence',
        ns: 'units'
    },
    {
        dataKey: 'directVerticalRatio',
        unitKey: Personalizer.getVerticalRatioUnitKey(),
        labelKey: 'label_vert_ratio',
        ns: 'units'
    },
    {
        dataKey: 'directWheelchairCadence',
        unitKey: 'wheelchair_cadence_unit',
        labelKey: 'label_wheelchair_cadence',
        ns: 'units'
    },
    {
        dataKey: 'directJumpRopeCadence',
        unitKey: 'bike_cadence_unit',
        labelKey: 'label_speed',
        ns: 'units'
    }
];
